// components/VeroFalsoPills.tsx — V28 blueprint C6.
// Maps to the prototype `.pill.vero` / `.pill.falso` pair (v28-prototype/index.html
// 860-882; markup in screens/ripasso.html 38-51 + simula-exam.html 52-65).
//
// Two side-by-side answer pills, each ≥44px tall (TAP_TARGET_MIN). The signal is
// ALWAYS icon + color, never color alone (WCAG 1.4.1 / accessibility):
//   - Vero  → sage tint + check (✓) icon.
//   - Falso → rose tint + cross (✕) icon.
// A `selected` pill reads as picked via the filled tint + strong border, again
// reinforced by the persistent icon so the choice survives for color-blind users.
//
// Light-only: every value comes from `useTokens()` (tokens.mode resolves 'light').
// The sage/rose soft-fill + 45%-alpha resting border match the prototype, derived
// from the semantic `right`/`wrong` token family so re-theming stays centralized.
//
// Reduced-motion: the hover/press color transition is suppressed when the user (OS
// or in-app toggle) prefers reduced motion, via use-reduced-motion.ts.

import { useState, type JSX } from 'react';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import { TAP_TARGET_MIN } from '../tokens';

export type VeroFalsoChoice = 'Vero' | 'Falso';

export interface VeroFalsoPillsProps {
  /** Called with the chosen answer when a pill is pressed. */
  onPick: (choice: VeroFalsoChoice) => void;
  /** When set, that pill renders in its filled `selected` state. */
  selected?: VeroFalsoChoice | null;
  /** Disable both pills (e.g. after an answer is locked in). */
  disabled?: boolean;
}

/** Sage check (✓) for "Vero". */
function CheckIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={18}
      height={18}
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Rose cross (✕) for "Falso". */
function CrossIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={18}
      height={18}
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

interface PillSpec {
  choice: VeroFalsoChoice;
  label: string;
  icon: JSX.Element;
  /** semantic accent (sage / rose) */
  accent: string;
  /** contrast-safe text color */
  textColor: string;
  /** filled tint background (selected / hover) */
  softBg: string;
  /** resting border (~45% alpha of the accent, per prototype) */
  restBorder: string;
}

export function VeroFalsoPills({
  onPick,
  selected = null,
  disabled = false,
}: VeroFalsoPillsProps): JSX.Element {
  const t = useTokens();
  const reduceMotion = useReducedMotion();
  // Founder phone bug (2026-07-08): hover was applied by MUTATING the DOM node
  // (e.currentTarget.style.background = …). On touch, the tap fires mouseover
  // with no matching mouseout, and React's style-prop diff never rewrites a
  // property it believes is unchanged — so the highlight leaked onto the NEXT
  // question when the pills were reused. Hover is now REACT STATE: every render
  // derives the full style from props+state, so no imperative residue can survive.
  const [hovered, setHovered] = useState<VeroFalsoChoice | null>(null);

  // Sage/rose resting borders match the prototype's rgba(...,0.45). The semantic
  // hexes are the same constants the tokens carry (light mode), reused here so the
  // alpha border + the token both stay in lockstep if the palette ever shifts.
  const SAGE_BORDER_45 = 'rgba(46, 141, 111, 0.45)';
  const ROSE_BORDER_45 = 'rgba(197, 54, 91, 0.45)';

  const pills: PillSpec[] = [
    {
      choice: 'Vero',
      label: 'Vero',
      icon: <CheckIcon />,
      accent: t.right,
      textColor: t.rightText,
      softBg: t.rightSoft,
      restBorder: SAGE_BORDER_45,
    },
    {
      choice: 'Falso',
      label: 'Falso',
      icon: <CrossIcon />,
      accent: t.wrong,
      textColor: t.wrongText,
      softBg: t.wrongSoft,
      restBorder: ROSE_BORDER_45,
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {pills.map((p) => {
        const isSelected = selected === p.choice;
        const isHovered = hovered === p.choice && !disabled && !isSelected;
        return (
          <button
            key={p.choice}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
            onClick={() => {
              if (disabled) return;
              setHovered(null);
              onPick(p.choice);
            }}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: TAP_TARGET_MIN,
              padding: '0 16px',
              borderRadius: t.radius.md,
              background: isSelected || isHovered ? p.softBg : t.surface,
              border: `1.5px solid ${isSelected || isHovered ? p.accent : p.restBorder}`,
              color: p.textColor,
              fontFamily: t.fonts.body,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: -0.1,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !isSelected ? 0.55 : 1,
              transition: reduceMotion
                ? 'none'
                : 'background .16s, border-color .16s',
            }}
            onMouseEnter={() => setHovered(p.choice)}
            onMouseLeave={() => setHovered((h) => (h === p.choice ? null : h))}
          >
            {p.icon}
            <span>{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}
